import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Save,
  RefreshCw,
  X,
  AlertCircle
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import PaginatedSelect from '../../../components/Common/Select/PaginatedSelect';
import api from '../../../api/axios';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../../context/LoadingContext';
import styles from './PayrollForm.module.css';

import CustomSelect from '../../../components/Common/Select/CustomSelect';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 8 }, (_, i) => {
  const y = currentYear + 1 - i;
  return { value: y, label: String(y) };
});
const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const CreatePayroll = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: '',
    month: new Date().getMonth() + 1,
    year: currentYear,
    other_deduction: 0,
    other_deduction_remarks: '',
    remarks: ''
  });
  const [preview, setPreview] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [calcError, setCalcError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCalculate = useCallback(async () => {
    if (!formData.employee_id || !formData.month || !formData.year) {
      setPreview(null);
      setCalcError('');
      return;
    }

    setIsCalculating(true);
    setCalcError('');
    try {
      const response = await api.post('/payroll/calculate', formData, { skipLoader: true });
      if (response.data.success) {
        setPreview(response.data.data);
      }
    } catch (error) {
      setPreview(null);
      setCalcError(error.response?.data?.message || 'Failed to calculate payroll');
    } finally {
      setIsCalculating(false);
    }
  }, [formData]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleCalculate();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [handleCalculate]);

  const handleGenerate = async () => {
    if (!preview) {
      toast.error('Please ensure payroll is calculated successfully before generating.');
      return;
    }

    setIsGenerating(true);
    showLoader();
    try {
      const response = await api.post('/payroll/generate', formData);
      if (response.data.success) {
        toast.success('Payroll generated successfully');
        navigate('/employees/payroll');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payroll');
    } finally {
      setIsGenerating(false);
      hideLoader();
    }
  };

  return (
    <div className={`${styles['create-po-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs customItems={[
                { label: 'HR', path: '#' },
                { label: 'Payroll / Salary', path: '/employees/payroll' },
                { label: 'Create Payroll', path: '#' }
              ]} />
              <h1>Generate Payroll</h1>
              <p>Create a new payroll for an employee</p>
            </div>

            <div className={styles['header-actions-btns']}>
              <button type="button" onClick={() => navigate('/employees/payroll')} className={styles['btn-cancel']}>
                <X size={18} /> <span>Cancel</span>
              </button>
              <button type="button" onClick={handleGenerate} className={styles['btn-primary']} disabled={isGenerating || !preview || isCalculating}>
                {isGenerating ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
                <span>Generate Payroll</span>
              </button>
            </div>
          </div>

          <div className={styles['form-sections-grid']}>
            {/* General Details */}
            <div className={styles['form-card']}>
              <div className={styles['card-header-po']}>
                <h3>Payroll Configuration</h3>
              </div>
              <div className={styles['card-content']}>
                <div className={styles['form-grid-2']}>
                  <div className={`${styles['form-group']} ${styles['full-width']}`}>
                    <label>Select Employee <span className={styles['required-star']}>*</span></label>
                    <PaginatedSelect
                      endpoint="/employees-dropdown"
                      valueKey="id"
                      labelKey="employee_name"
                      placeholder="-- Select Employee --"
                      value={formData.employee_id}
                      onChange={(val) => setFormData(prev => ({ ...prev, employee_id: val || '' }))}
                      formatOptionLabel={(opt) => {
                        const name = opt.employee_name || opt.original?.employee_name || opt.label || '';
                        const code = opt.designation_name || opt.original?.designation_name || '';
                        return code ? `${name} (${code})` : name;
                      }}
                    />
                  </div>

                  <div className={styles['form-group']}>
                    <label>Month <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={months.find(m => m.value === Number(formData.month)) || null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, month: opt ? opt.value : '' }))}
                      options={months}
                      placeholder="Select Month"
                      isSearchable={false}
                    />
                  </div>

                  <div className={styles['form-group']}>
                    <label>Year <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={yearOptions.find(y => y.value === Number(formData.year)) || null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, year: opt ? opt.value : '' }))}
                      options={yearOptions}
                      placeholder="Select Year"
                      isSearchable={false}
                    />
                  </div>

                  <div className={styles['form-group']}>
                    <label>Other Deduction Amount</label>
                    <div className={styles['currency-input-wrapper']}>
                      <span className={styles['currency-symbol']}>₹</span>
                      <input
                        type="number"
                        name="other_deduction"
                        value={formData.other_deduction}
                        onChange={handleChange}
                        className={`${styles['form-input']} ${styles['currency-input']}`}
                        min="0"
                      />
                    </div>
                  </div>

                  <div className={styles['form-group']}>
                    <label>Other Deduction Remarks</label>
                    <input
                      type="text"
                      name="other_deduction_remarks"
                      value={formData.other_deduction_remarks}
                      onChange={handleChange}
                      className={styles['form-input']}
                      placeholder="e.g., Canteen charges"
                    />
                  </div>

                  <div className={`${styles['form-group']} ${styles['full-width']}`}>
                    <label>Remarks / Notes</label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      className={styles['form-textarea']}
                      rows="3"
                      placeholder="Additional notes for this payroll..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Calculation Preview */}
            <div className={styles['form-card']}>
              <div className={styles['card-header-po']} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Calculation Breakdown</h3>
                {isCalculating && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#2563eb', fontWeight: '500' }}>
                    <RefreshCw size={16} className="spin" />
                    <span>Calculating...</span>
                  </div>
                )}
              </div>

              <div className={styles['card-content']} style={{ minHeight: '400px', position: 'relative' }}>
                {!formData.employee_id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', padding: '40px' }}>
                    <p>Please select an employee to view their payroll breakdown.</p>
                  </div>
                ) : calcError ? (
                  <div style={{ padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <AlertCircle size={20} style={{ marginTop: '2px' }} />
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontWeight: '600' }}>Error</h4>
                      <p style={{ margin: 0, fontSize: '14px' }}>{calcError}</p>
                    </div>
                  </div>
                ) : isCalculating && !preview ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#64748b' }}>
                    <RefreshCw size={24} className="spin" style={{ marginRight: '8px', color: '#2563eb' }} />
                    <p style={{ margin: 0, fontWeight: '500' }}>Calculating breakdown...</p>
                  </div>
                ) : preview ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', opacity: isCalculating ? 0.5 : 1, transition: 'opacity 0.2s ease-in-out' }}>

                    {/* Attendance Highlights */}
                    <div className={styles['attendance-grid']}>
                      <div style={{ backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Days</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>{preview.period?.total_days_in_month || 0}</div>
                      </div>
                      <div style={{ backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#059669', marginBottom: '4px' }}>Present</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#047857' }}>{preview.attendance?.present_days || 0}</div>
                      </div>
                      <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#e11d48', marginBottom: '4px' }}>Leave/Absent</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#be123c' }}>{preview.attendance?.leave_days || 0}</div>
                      </div>
                      <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '4px' }}>Late</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#b45309' }}>{preview.attendance?.late_days || 0}</div>
                      </div>
                      <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#16a34a', marginBottom: '4px' }}>OT Days</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#15803d' }}>{preview.overtime?.ot_days || 0}</div>
                      </div>
                    </div>

                    <div className={styles['earnings-deductions-grid']}>
                      {/* Earnings */}
                      <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h4 style={{ margin: '0 0 16px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Earnings</h4>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#475569' }}>Base Salary</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.salary?.basic_salary || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#475569' }}>Per Day Salary</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.salary?.per_day_salary || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#475569' }}>Earned Salary ({preview.attendance?.working_days || 0} days)</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.salary?.earned_salary || 0}</span>
                        </div>
                        {preview.overtime?.ot_amount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#475569' }}>Overtime ({preview.overtime?.ot_days} days)</span>
                            <span style={{ fontWeight: '500' }}>₹{preview.overtime?.ot_amount || 0}</span>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #cbd5e1' }}>
                          <span style={{ color: '#0f172a', fontWeight: '600' }}>Gross Salary</span>
                          <span style={{ color: '#059669', fontWeight: '700' }}>₹{preview.gross_salary || 0}</span>
                        </div>
                      </div>

                      {/* Deductions */}
                      <div style={{ padding: '16px', backgroundColor: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                        <h4 style={{ margin: '0 0 16px 0', color: '#991b1b', borderBottom: '1px solid #fecaca', paddingBottom: '8px' }}>Deductions</h4>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>PF</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.deductions?.pf_deduction || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>ESIC</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.deductions?.esic_deduction || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>Professional Tax</span>
                          <span style={{ fontWeight: '500' }}>₹{preview.deductions?.professional_tax || 0}</span>
                        </div>
                        {preview.deductions?.advance_deduction > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#7f1d1d' }}>Advance Deducted</span>
                            <span style={{ fontWeight: '500' }}>₹{preview.deductions?.advance_deduction}</span>
                          </div>
                        )}
                        {preview.deductions?.loan_emi_deduction > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#7f1d1d' }}>Loan EMI</span>
                            <span style={{ fontWeight: '500' }}>₹{preview.deductions?.loan_emi_deduction}</span>
                          </div>
                        )}
                        {preview.deductions?.other_deduction > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#7f1d1d' }}>Other Deductions</span>
                            <span style={{ fontWeight: '500' }}>₹{preview.deductions?.other_deduction}</span>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #fca5a5' }}>
                          <span style={{ color: '#991b1b', fontWeight: '600' }}>Total Deductions</span>
                          <span style={{ color: '#dc2626', fontWeight: '700' }}>₹{preview.total_deductions || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Net Total */}
                    <div className={styles['net-payable-card']}>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0', color: '#1e3a8a', fontSize: '18px' }}>Net Payable Salary</h3>
                        <p style={{ margin: 0, color: '#3b82f6', fontSize: '14px' }}>Gross Salary - Total Deductions</p>
                      </div>
                      <span className={styles['net-payable-amount']}>
                        ₹{Math.max(0, parseFloat(preview.net_salary || 0)).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                    <RefreshCw size={24} className="spin" style={{ marginRight: '8px' }} />
                    <p>Loading preview...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreatePayroll;
